import {NgIf, NgClass, NgStyle, NgFor, AsyncPipe, DecimalPipe} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  ViewEncapsulation,
  OnInit,
  ViewChild,
  ChangeDetectorRef
} from '@angular/core';
import {DomSanitizer} from '@angular/platform-browser';
import {MatMenuTrigger, MatMenuModule} from '@angular/material/menu';
import {MatIconModule} from '@angular/material/icon';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatButtonModule} from '@angular/material/button';
import {TranslateModule} from '@ngx-translate/core';
import * as _ from 'lodash';
import {noop} from 'rxjs';

import {Q9HtmlDecoderPipe, Q9TransformPipe} from '@q9elements/ui-kit/common';
import {SidebarService} from '../../../../editor-interface/sidebar/sidebar.service';
import {AttachmentsService} from '../../../../diagram-interaction/attachments/attachments.service';
import {EditorRegistryService} from '../../../mx-graph/editor/editor-registry.service';
import {AuthService} from '../../../../core/auth/auth.service';
import {
  DiagramJsonHandlerService,
  IActJson
} from '../../../../diagram-data/diagram-json-handler.service';
import {ModeDataService} from '../../../../editor-state/mode-data.service';
import {isUrlAttachment} from '../../../helpers/attachments.util';
import {InfoStyle} from '../../../styles/styles.model';
import {ActivityTabsInViewEnum, ActivityTabsInEditEnum} from '../shape.model';
import {RecentlySeenService} from '../../../../shared/service/recently-seen.service';
import {UserGroupsFilterPipe} from '../../../helpers/user-groups-filter.pipe';

@Component({
  selector: 'app-activity-paperclip',
  templateUrl: './activity-paperclip.component.html',
  styleUrls: ['./activity-paperclip.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgIf,
    MatButtonModule,
    MatTooltipModule,
    NgClass,
    NgStyle,
    MatIconModule,
    MatMenuModule,
    NgFor,
    AsyncPipe,
    DecimalPipe,
    TranslateModule,
    Q9HtmlDecoderPipe,
    UserGroupsFilterPipe,
    Q9TransformPipe
  ]
})
export class ActivityPaperclipComponent implements OnInit {
  attachmentsOpened = false;
  isProSpace: boolean;
  userGroups: string[] = [];
  allAttachments = [];

  private _source: IActJson;

  @Input()
  set source(value: IActJson) {
    this._source = value;
    this.allAttachments = [
      ...(value?.attachments || []),
      ...this.filterNonClosedAttachments(value?.requirementAttachments),
      ...this.filterNonClosedAttachments(value?.storyAttachments),
      ...(value?.refModelAttachments || [])
    ];
  }

  get source(): IActJson {
    return this._source;
  }
  @Input() infoStyle?: InfoStyle | null;
  @ViewChild(MatMenuTrigger)
  private menuTrigger: MatMenuTrigger;

  public getAttachmentIcon = (item): string => {
    if (item.type === 'image') {
      return 'image';
    }

    if (item.type === 'data-table' || item.type === 'datatable') {
      return 'data-tables';
    }

    if (item.type === 'url' || item.type === 'url_entry') {
      return 'link';
    }

    if (item.type === 'text' || item.type === 'note') {
      return 'note';
    }

    if (item?.reqId || item?.storyId) {
      return 'changes';
    }

    if (item?.refModel) {
      return 'rm';
    }

    return '';
  };

  constructor(
    private sidebarSvc: SidebarService,
    private attachmentsSvc: AttachmentsService,
    private authSvc: AuthService,
    private diagramJsonSvc: DiagramJsonHandlerService,
    private registry: EditorRegistryService,
    private sanitizer: DomSanitizer,
    public modeData: ModeDataService,
    private cd: ChangeDetectorRef,
    private readonly recentlySeenSvc: RecentlySeenService
  ) {}

  ngOnInit(): void {
    this.userGroups = _.get(this.authSvc.getUserDataSync(), 'userInTeam.groups', []);
    this.isProSpace = this.diagramJsonSvc.isPro();
  }

  openAttachments() {
    // const attCount = this.totalAttachmentsCount();
    // const resCount = this.totalResourceCount();

    // this.sidebarSvc.refresh(this.source.mxKey, 2);

    // const tabOffset = this.modeData.isEditMode() ? 1 : 0;
    // this.sidebarSvc.refresh(this.source.mxKey, attCount ? 1 + tabOffset : tabOffset);

    // this.sidebarSvc.rightSidebar.open();

    // const graph = this.registry.getGraph();
    // if (!resCount && attCount === 1 && !graph.isEnabled()) {
    //   const attachment = this.singleAttachment();
    //   const isPro = this.diagramJsonSvc.isPro();
    //   if (!isPro || this.hasPermissionForAttachment(attachment)) {
    //     this.openSingleAttachment(attachment);
    //   }
    // }

    const tabs = this.tabUtil();

    if (!this.modeData.isEditMode()) {
      if (tabs.isDocumentationsPresent) {
        return this.openRsbAndTab(ActivityTabsInViewEnum.DOCUMENTATIONS);
      }

      if (tabs.isChangesPresent) {
        return this.openRsbAndTab(ActivityTabsInViewEnum.CHANGES);
      }
    } else {
      if (tabs.isDocumentationsPresent) {
        return this.openRsbAndTab(ActivityTabsInEditEnum.DOCUMENTATIONS);
      }

      if (tabs.isChangesPresent) {
        return this.openRsbAndTab(ActivityTabsInEditEnum.CHANGES);
      } else {
        return this.openRsbAndTab(ActivityTabsInEditEnum.THEME);
      }
    }
  }

  private filterNonClosedAttachments<
    T extends {
      status?: {category?: string};
      reqId?: number;
      storyId?: number;
    }
  >(attachments?: T[]): T[] {
    return (
      attachments
        ?.filter(
          attachment => !['closed', 'closed_implemented'].includes(attachment.status?.category)
        )
        .sort((a, b) => {
          const aId = a.reqId ?? a.storyId ?? 0;
          const bId = b.reqId ?? b.storyId ?? 0;

          return bId - aId;
        }) || []
    );
  }

  private tabUtil() {
    const isDocumentationsPresent = !!(
      (this.source.attachments || []).length + (this.source.refModelAttachments || []).length
    );

    const isChangesPresent = !!(
      (this.source.requirementAttachments || []).length +
      (this.source.storyAttachments || []).length
    );

    return {
      isDocumentationsPresent,
      isChangesPresent
    };
  }

  private openRsbAndTab(tab) {
    this.sidebarSvc.refresh(this.source.mxKey, tab);

    return this.sidebarSvc.rightSidebar.open();
  }

  public openSingleAttachment(attachment) {
    if (attachment?.reqId || attachment?.storyId) {
      return this.openRsbAndTab(ActivityTabsInViewEnum.CHANGES);
    }

    const graph = this.registry.getGraph();
    const type = attachment?.refModel ? 'refModelNode' : '';

    attachment = this.prepareAttachment(attachment, type);

    if (!isUrlAttachment(attachment)) {
      this.attachmentsSvc.openAttachment(
        graph.model.getCell(this.source.mxKey),
        attachment,
        type || attachment.type
      );
    } else {
      const urlEntryId = attachment?.urlEntry?.id;

      if (urlEntryId) {
        this.recentlySeenSvc.captureRecentlyViewedURL(urlEntryId).subscribe(noop);
      }

      this.openLink(attachment.value);
    }
  }

  private prepareAttachment(attachment, type?: string) {
    if (isUrlAttachment(attachment)) {
      return attachment;
    }

    if (type) {
      attachment.type = type;
    }

    attachment._id = String(attachment.mxKey || attachment.id);

    return attachment;
  }

  private openLink(link) {
    if (!link) {
      return;
    }

    if (!link.match(/^https?:\/\//i)) {
      if (!link.match(/:/)) {
        link = 'http://' + link;
      }
    }

    const trustUrlRegExp = new RegExp(/^\s*(https?|ftp|mailto|iwl):/, `im`);
    const trustUrl = trustUrlRegExp.test(link);

    link = <string>this.sanitizer.bypassSecurityTrustUrl(link);

    if (link['changingThisBreaksApplicationSecurity']) {
      if (trustUrl) {
        return window.open(link['changingThisBreaksApplicationSecurity']);
      } else {
        return;
      }
    }

    window.open(link, '_blank');
  }

  // private singleAttachment() {
  //   if (this.source.attachments && this.source.attachments[0]) {
  //     return this.source.attachments[0];
  //   }
  //   if (this.source.requirementAttachments && this.source.requirementAttachments[0]) {
  //     this.source.requirementAttachments[0].type = 'requirement';
  //     return this.source.requirementAttachments[0];
  //   }
  //   if (this.source.refModelAttachments && this.source.refModelAttachments[0]) {
  //     this.source.refModelAttachments[0].type = 'refModelNode';
  //     return this.source.refModelAttachments[0];
  //   }
  // }

  // private totalAttachmentsCount() {
  //   const rmnCount = this.source.refModelAttachments ? this.source.refModelAttachments.length : 0;
  //   const reqCount = this.source.requirementAttachments ? this.source.requirementAttachments.length : 0;
  //   const generalCount = this.source.attachments ? this.source.attachments.length : 0;
  //   return rmnCount + reqCount + generalCount;
  // }

  // private totalResourceCount() {
  //   return this.source.resources ? this.source.resources.length : 0;
  // }

  openMenu() {
    // workaround to move context menu left
    const className = 'cdk-overlay-connected-position-bounding-box';
    const menus = document.getElementsByClassName(className) as HTMLCollection;
    const targetMenu = _.last(menus) as HTMLElement;

    if (!targetMenu) {
      return;
    }

    targetMenu.style.left = targetMenu.offsetLeft + 20 + 'px';
    this.attachmentsOpened = true;
    this.menuTrigger.openMenu();
  }

  onMenuClose() {
    setTimeout(() => {
      this.attachmentsOpened = false;
      this.cd.detectChanges();
    }, 400);
  }

  track(index, item) {
    return item.id;
  }
}
